import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, Mail, Download, RefreshCw, Search, Crown, Store, Phone } from 'lucide-react';
import { toast } from 'sonner';
import GoogleApiUsageCard from '@/components/admin/GoogleApiUsageCard';
import AdminCommandCenter from '@/components/admin/AdminCommandCenter';
import AdminGrantFinderPanel from '@/components/admin/AdminGrantFinderPanel';
import AdminMedullaPanel from '@/components/admin/AdminMedullaPanel';
import AdminSinglesNightPanel from '@/components/admin/AdminSinglesNightPanel';
import AdminAutoAdLog from '@/components/admin/AdminAutoAdLog';
import { Megaphone } from 'lucide-react';
import MatrixToggle from '@/components/MatrixToggle';
import MatrixRain from '@/components/MatrixRain';
import VersionBadge from '@/components/VersionBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [adminKey, setAdminKey] = useState('');
  const [isAuthed, setIsAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  const [overview, setOverview] = useState(null);
  const [systemHealth, setSystemHealth] = useState(null);
  const [patrons, setPatrons] = useState([]);
  const [barOwners, setBarOwners] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('patrons');

  const getKey = () => adminKey || localStorage.getItem('nightbuzz_admin_key');

  const authenticate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, {
        email: adminEmail,
        password: adminPassword
      });
      const key = res.data.admin_key;
      setAdminKey(key);
      localStorage.setItem('nightbuzz_admin_key', key);
      setIsAuthed(true);
      toast.success('Welcome, Admin!');
      fetchAllWithKey(key);
    } catch {
      toast.error('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWithKey = async (key) => {
    try {
      const [pRes, oRes, sRes, aggRes, ovRes, hRes] = await Promise.all([
        axios.get(`${API}/admin/patrons?admin_key=${key}`),
        axios.get(`${API}/admin/bar-owners?admin_key=${key}`),
        axios.get(`${API}/admin/stats?admin_key=${key}`),
        axios.get(`${API}/admin/aggregates/all?admin_key=${key}`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/overview?admin_key=${key}`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/health?admin_key=${key}`).catch(() => ({ data: null })),
      ]);
      setPatrons(pRes.data.patrons || []);
      setBarOwners(oRes.data.bar_owners || []);
      setStats(sRes.data);
      setAggregates(aggRes.data);
      setOverview(ovRes.data);
      setSystemHealth(hRes.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchAll = async () => {
    const key = getKey();
    if (!key) return;
    fetchAllWithKey(key);
  };

  useEffect(() => {
    const savedKey = localStorage.getItem('nightbuzz_admin_key');
    if (savedKey) {
      setAdminKey(savedKey);
      setIsAuthed(true);
      fetchAllWithKey(savedKey);
      // Live-sync analytics across all admin windows/monitors every 60s
      const iv = setInterval(() => fetchAllWithKey(savedKey), 60000);
      return () => clearInterval(iv);
    }
  }, []);

  const getFiltered = (list) => {
    if (!searchTerm) return list;
    const term = searchTerm.toLowerCase();
    return list.filter(p =>
      p.username?.toLowerCase().includes(term) ||
      p.email?.toLowerCase().includes(term) ||
      p.business_name?.toLowerCase().includes(term)
    );
  };

  const currentList = activeTab === 'patrons' ? getFiltered(patrons) : getFiltered(barOwners);

  const copyEmails = () => {
    const emails = currentList.map(p => p.email).filter(Boolean).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success(`${currentList.length} emails copied!`);
  };

  const exportCSV = () => {
    const key = getKey();
    const endpoint = activeTab === 'patrons' ? 'patrons' : 'bar-owners';
    window.open(`${API}/admin/${endpoint}/export?admin_key=${key}`, '_blank');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    const now = new Date();
    const days = Math.floor((now - d) / 86400000);
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
  };

  // Login screen
  if (!isAuthed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <MatrixToggle />
        <Card className="glass p-8 border border-slate-700 max-w-md w-full" data-testid="admin-login-card">
          <div className="text-center mb-6">
            <img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/wingman/8256134e-a862-4a68-83b5-73118a4b2925/attachments/aaceaeff3f204ebda14fd8878571981f_Screenshot_20260728_005319_Gallery.jpg"
              alt="NightBuZz"
              data-testid="admin-header-logo"
              style={{ width: 220, maxWidth: '80vw', height: 'auto', objectFit: 'contain', display: 'block', margin: '0 auto 30px', filter: 'drop-shadow(0 0 20px rgba(255,215,0,0.9))', borderRadius: 12 }}
              onError={(e) => { e.target.src = '/nightbuzz-logo.png'; }}
            />
            <h1 className="text-2xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-slate-400">Sign in with your admin credentials</p>
          </div>
          <div className="space-y-4">
            <Input
              type="email"
              placeholder="Admin Email"
              value={adminEmail}
              onChange={(e) => setAdminEmail(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white"
              data-testid="admin-email-input"
            />
            <Input
              type="password"
              placeholder="Password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              className="bg-slate-800 border-slate-600 text-white"
              onKeyPress={(e) => e.key === 'Enter' && authenticate()}
              data-testid="admin-password-input"
            />
            <Button
              onClick={authenticate}
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Verifying...' : 'Sign In'}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative">
      <MatrixRain />
      <MatrixToggle />
      <VersionBadge variant="admin-v1" />
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-white hover:bg-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold text-white">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={fetchAll} className="text-white hover:bg-white/10" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <img
              src="https://customer-assets-lxgj4vgw.emergentagent.net/wingman/8256134e-a862-4a68-83b5-73118a4b2925/attachments/aaceaeff3f204ebda14fd8878571981f_Screenshot_20260728_005319_Gallery.jpg"
              alt="NightBuZz"
              data-testid="admin-nav-logo"
              className="h-10"
              style={{ objectFit: 'contain', filter: 'drop-shadow(0 0 8px rgba(255,215,0,0.9))', borderRadius: 6 }}
              onError={(e) => { e.target.src = '/nightbuzz-logo.png'; }}
            />
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Quick Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-4 mb-8">
            <Card className="glass p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Users className="w-8 h-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white" data-testid="total-patrons">{stats.total_patrons}</p>
                  <p className="text-sm text-slate-400">Patrons</p>
                </div>
              </div>
            </Card>
            <Card className="glass p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Store className="w-8 h-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-white" data-testid="total-owners">{stats.total_bar_owners}</p>
                  <p className="text-sm text-slate-400">Bar Owners</p>
                </div>
              </div>
            </Card>
            <Card className="glass p-4 border border-slate-700">
              <div className="flex items-center gap-3">
                <Mail className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white" data-testid="total-emails">{patrons.length + barOwners.length}</p>
                  <p className="text-sm text-slate-400">Total Emails</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Google API Usage */}
        <div className="mb-6">
          <GoogleApiUsageCard adminKey={getKey()} />
        </div>

        {/* OS Command Center — dense feature/ecosystem view */}
        <div className="mb-8">
          <AdminCommandCenter stats={stats} aggregates={aggregates} overview={overview} systemHealth={systemHealth} adminKey={getKey()} />
          <AdminGrantFinderPanel adminKey={getKey()} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            onClick={() => setActiveTab('patrons')}
            className={activeTab === 'patrons' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}
            data-testid="patrons-tab"
          >
            <Users className="w-4 h-4 mr-2" />
            Patrons ({patrons.length})
          </Button>
          <Button
            onClick={() => setActiveTab('owners')}
            className={activeTab === 'owners' ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}
            data-testid="owners-tab"
          >
            <Store className="w-4 h-4 mr-2" />
            Bar Owners ({barOwners.length})
          </Button>
          <Button
            onClick={() => setActiveTab('medulla')}
            className={activeTab === 'medulla' ? 'bg-green-600 hover:bg-green-700' : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}
            data-testid="medulla-tab"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Medulla
          </Button>
        </div>

        {activeTab === 'medulla' ? (
          <Card className="glass p-6 border border-slate-700 space-y-4">
            <AdminSinglesNightPanel />
            <AdminMedullaPanel />
            <AdminAutoAdLog />
          </Card>
        ) : (
        <>
        {/* Main Card */}
        <Card className="glass p-6 border border-slate-700">
          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6 items-start sm:items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800 border-slate-600 text-white"
                data-testid="search-input"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={copyEmails} size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="copy-emails-btn">
                <Mail className="w-4 h-4 mr-2" />
                Copy Emails ({currentList.length})
              </Button>
              <Button onClick={exportCSV} size="sm" variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10" data-testid="export-csv-btn">
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Email List */}
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="signup-table">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-3 text-slate-400 font-medium">#</th>
                  <th className="text-left p-3 text-slate-400 font-medium">
                    {activeTab === 'patrons' ? 'Username' : 'Business Name'}
                  </th>
                  <th className="text-left p-3 text-slate-400 font-medium">Email</th>
                  {activeTab === 'patrons' && (
                    <th className="text-left p-3 text-slate-400 font-medium">Emergency Contact</th>
                  )}
                  <th className="text-left p-3 text-slate-400 font-medium">Signed Up</th>
                  {activeTab === 'owners' && (
                    <th className="text-left p-3 text-slate-400 font-medium">Bar</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {currentList.map((item, idx) => (
                  <tr key={item.id || idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="p-3 text-slate-500">{idx + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shrink-0">
                          <span className="text-white text-xs font-bold">
                            {(item.username || item.business_name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-white font-medium text-sm">
                          {activeTab === 'patrons' ? item.username : item.business_name}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      <a href={`mailto:${item.email}`} className="text-indigo-400 hover:text-indigo-300 hover:underline text-sm" data-testid={`email-link-${idx}`}>
                        {item.email}
                      </a>
                    </td>
                    {activeTab === 'patrons' && (
                      <td className="p-3">
                        {item.emergency_contact_first_name ? (
                          <div className="text-xs space-y-0.5">
                            <div className="text-white font-medium">{item.emergency_contact_first_name} {item.emergency_contact_last_name}</div>
                            <div className="text-slate-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {item.emergency_contact_phone || 'N/A'}
                            </div>
                            <div className="text-purple-400 capitalize">{item.emergency_contact_relationship || ''}</div>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-xs">Not provided</span>
                        )}
                      </td>
                    )}
                    <td className="p-3 text-slate-400 text-sm">{formatDate(item.created_at)}</td>
                    {activeTab === 'owners' && (
                      <td className="p-3">
                        <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                          {item.bar_name || 'Unclaimed'}
                        </Badge>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {currentList.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No {activeTab === 'patrons' ? 'patrons' : 'bar owners'} found</p>
            </div>
          )}
        </Card>
        </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
